import Slider from 'components/Slider'
import SnapCarousel from 'components/SnapCarousel'
import LazyFlightCard from 'components/LazyFlightCard'
import { useCurrency } from 'hooks/useCurrency'
import { useIsMobile } from 'hooks/useIsMobile'
import { useIsClient } from 'hooks/useIsClient'
import * as S from './PromotedFlights.styled'
import { useAppState } from 'context/AppContext'
import { useMemo, useState, useRef, useEffect } from 'react'
import { InfoCircleOutline } from '@enuygun/icons'
import { timeAgo } from 'utils/timeAgo'
import { PromotedFlightCard } from '@enuygun/frontend-components'
import { Button, Dialog, Tooltip } from '@enuygun/design-system'
import { i18n, useTranslation } from 'next-i18next'
import dayjs from 'dayjs'
import { PromotedFlightsProps, PromotedFlight } from './PromotedFlights.typed'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'
import { buildPromotedFlightsSeeAllUrl, findFirstPromotedFlightSearchUrl } from 'utils/wingieApiLinks'
import { useDeviceType } from 'hooks/useDeviceType'
import Celebrating10YearsBadge from './Celebrating10YearsBadge'
import ConfettiDecoration, { ConfettiOnce } from './ConfettiDecoration'
import DesktopGlowBg from './DesktopGlowBg'
import MobileGlowBg from './MobileGlowBg'

// Sabitler
const TR_MARKET = 'tr'
const TR_BASE_URL = 'https://www.enuygun.com/ucak-bileti/firsat-ucuslari/'
const PROMOTED_ICON_URL = 'https://cdn2.enuygun.com/media/lib/uploads/image/promoted-icon-65148.png'
const PRELOAD_COUNT_MOBILE = 2
const PRELOAD_COUNT_TABLET = 3
const PRELOAD_COUNT_DESKTOP = 4
const SNAP_CAROUSEL_OPTIONS = {
  perPage: 1.25,
  gap: 16,
}

const highlightAnniversaryTens = (text: string) => {
  const nodes: React.ReactNode[] = []
  let lastIndex = 0
  let matchCount = 0
  const re = /10/g
  let match: RegExpExecArray | null

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }
    matchCount += 1
    nodes.push(<S.AnniversaryHighlight key={`anniversary-10-${matchCount}`}>{match[0]}</S.AnniversaryHighlight>)
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return nodes
}
const SNAP_CAROUSEL_TABLET_OPTIONS = {
  perPage: 2,
  gap: 16,
}
const SLIDER_OPTIONS = {
  arrows: true,
  pagination: true,
  fixedWidth: undefined,
  gap: 15,
  perPage: 4,
  padding: 0,
}

const CARD_BUTTON_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  textAlign: 'inherit',
  width: '100%',
  font: 'inherit',
  color: 'inherit',
}

const PROMOTE_RATE_STYLE: React.CSSProperties = {
  direction: 'ltr',
  textAlign: 'left',
  display: 'inline-block',
  unicodeBidi: 'isolate',
}

const formatPromoteRate = (promoteRate: number, isWingie: boolean, isRTL: boolean): React.ReactNode => {
  const formattedPrice = isWingie || isRTL ? `-${promoteRate}%` : `-%${promoteRate}`
  return <span style={PROMOTE_RATE_STYLE}>{formattedPrice}</span>
}

const renderDiscountPrice = (price: string) => <div style={{ direction: 'ltr', textAlign: 'left' }} dangerouslySetInnerHTML={{ __html: price }}></div>

const renderPrice = (price: string) => <div dangerouslySetInnerHTML={{ __html: price }}></div>

const getPreloadCount = (isMobile: boolean, isTablet: boolean) => {
  if (isMobile) return PRELOAD_COUNT_MOBILE
  if (isTablet) return PRELOAD_COUNT_TABLET
  return PRELOAD_COUNT_DESKTOP
}

const getModalItems = (t: (key: string) => string, market?: string) => [
  t('promotedFlights.modal.firstItem'),
  t('promotedFlights.modal.secondItem'),
  t('promotedFlights.modal.thirdItem'),
  ...(market === 'tr' ? [t('promotedFlights.modal.fourthItem'), t('promotedFlights.modal.fifthItem')] : []),
]

// Preload kartları için özel wrapper - SSR safe
const EagerPromotedFlightCard: React.FC<{ children: React.ReactNode; shouldPreload: boolean }> = ({ children, shouldPreload }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (shouldPreload && wrapperRef.current) {
      // Client-side'da çalışır
      const images = wrapperRef.current.querySelectorAll('img[loading="lazy"]')
      images.forEach((img) => {
        img.setAttribute('loading', 'eager')
      })
    }
  }, [shouldPreload])

  return (
    <div ref={wrapperRef} style={{ display: 'contents' }}>
      {children}
    </div>
  )
}

interface PromotedFlightCardItemProps {
  flightData: PromotedFlight
  index: number
  isPreload: boolean
  isMobile: boolean
  isRTL: boolean
  isWingie: boolean
  market?: string
  shouldShowChristmasBadge: boolean
  visaFreeStatus: boolean | undefined
  activeTabType: string
  currencyFormatter: ReturnType<typeof useCurrency>
  currencyIso?: string
  isClient: boolean
  t: any
  getTranslations: (countryCode?: string) => {
    popularRoutesImage: (origin: string, destination: string) => string
    roundTrip: string
    oneWay: string
    visaFreeText: string
  }
  onFlightClick: (index: number, type: string) => void
}

const PromotedFlightCardItem: React.FC<PromotedFlightCardItemProps> = ({
  flightData,
  index,
  isPreload,
  isMobile,
  isRTL,
  isWingie,
  market,
  shouldShowChristmasBadge,
  visaFreeStatus,
  activeTabType,
  currencyFormatter,
  currencyIso,
  isClient,
  t,
  getTranslations,
  onFlightClick,
}) => {
  const handleClick = () => onFlightClick(index, activeTabType)

  const cardContent = (
    <button type="button" key={flightData.id} dir={isRTL ? 'rtl' : 'ltr'} onClick={handleClick} style={CARD_BUTTON_STYLE}>
      <PromotedFlightCard
        {...flightData}
        market={market}
        isChristmas={shouldShowChristmasBadge}
        //@ts-ignore
        promoteRate={formatPromoteRate(flightData.promoteRate, isWingie, isRTL)}
        searchUrl={flightData.searchUrl}
        formattedDepartureDate={dayjs(flightData?.departureDate?.date).format('D MMMM')}
        //@ts-ignore
        formattedReturnDate={flightData.returnDate ? dayjs(flightData.returnDate.date).format('D MMMM') : null}
        formattedTimeAgo={isClient ? timeAgo(new Date(flightData?.createdAt?.date), t) : ''}
        //@ts-ignore
        formattedAverageAmount={currencyFormatter?.conversion ? currencyFormatter.summaryFormatHtml(flightData.averageAmount, 0, false, true, currencyIso) + '*' : null}
        formattedLowestPrice={currencyFormatter?.conversion ? currencyFormatter.summaryFormatHtml(flightData.lowestPrice, 0, false, true, currencyIso) : undefined}
        translations={getTranslations(flightData.destinationCountryCode)}
        airlineName={flightData?.departureAirline?.name}
        airlineLogo={flightData?.departureAirline?.image}
        returnAirlineName={flightData?.returnDate ? flightData?.returnAirline?.name : undefined}
        returnAirlineLogo={flightData?.returnDate ? flightData?.returnAirline?.image : undefined}
        renderDiscountPrice={renderDiscountPrice}
        renderPrice={renderPrice}
        visaFree={visaFreeStatus}
      />
    </button>
  )

  return (
    <LazyFlightCard key={flightData.id} index={index} isMobile={!!isMobile} dataTestId="promoted-flight-card" preload={isPreload}>
      <EagerPromotedFlightCard shouldPreload={isPreload}>{cardContent}</EagerPromotedFlightCard>
    </LazyFlightCard>
  )
}

const ModalItemsList: React.FC<{ market?: string; testIdPrefix: string }> = ({ market, testIdPrefix }) => {
  const { t } = useTranslation('common')
  const modalItems = getModalItems(t, market)

  return (
    <S.StyledList>
      {modalItems.map((item, index) => (
        <S.StyledListItem key={`${testIdPrefix}-${item}`} data-testid={`${testIdPrefix}-${index + 1}`}>
          {item}
        </S.StyledListItem>
      ))}
    </S.StyledList>
  )
}

// Desktop modal dialog
const InfoDialog: React.FC<{ isOpen: boolean; onClose: () => void; market?: string }> = ({ isOpen, onClose, market }) => {
  const { t } = useTranslation('common')

  return (
    <Dialog width="50%" open={isOpen} onClose={onClose} footer={false} contentStyle={{ padding: 20 }}>
      <S.ModalContent onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle data-testid="promoted-flight-modal-title">{t('promotedFlights.modal.title')}</S.ModalTitle>
        </S.ModalHeader>
        <S.ModalBody data-testid="promoted-flight-modal-info-list">
          <ModalItemsList market={market} testIdPrefix="promoted-flight-modal-item" />
        </S.ModalBody>
      </S.ModalContent>
    </Dialog>
  )
}

interface SeeAllButtonProps {
  mobileBlock?: boolean
  isAnniversary: boolean
  isMobile: boolean
  market?: string
  isWingie: boolean
  hasPromotedFlights: boolean
  seeAllUrl: string | null
  t: (key: string) => string
}

const SeeAllButton: React.FC<SeeAllButtonProps> = ({
  mobileBlock = false,
  isAnniversary,
  isMobile,
  market,
  isWingie,
  hasPromotedFlights,
  seeAllUrl,
  t,
}) => {
  const shouldShow = market === 'tr' || (isWingie && hasPromotedFlights)
  if (!shouldShow) return <></>

  const text = t('promotedFlights.seeAll')

  if (!seeAllUrl) return <></>

  return (
    <S.ButtonWrapper $anniversary={isAnniversary} $mobileBlock={mobileBlock} data-testid="promoted-flight-see-all-button-wrapper">
      <Button
        block={mobileBlock || !!isMobile}
        style={{ zIndex: 2 }}
        as="a"
        href={seeAllUrl}
        size={mobileBlock || isMobile ? 'default' : 'small'}
        palette="gray"
        shape="outline"
        data-testid="promoted-flight-see-all-button"
      >
        {text}
      </Button>
    </S.ButtonWrapper>
  )
}

interface TabContentProps {
  data: PromotedFlight[]
  activeTab: number
  activeTabType: string
  christmasFlightIds: Set<unknown>
  isMobile: boolean
  isTablet: boolean
  isRTL: boolean
  isWingie: boolean
  isAnniversary: boolean
  isClient: boolean
  market?: string
  currencyFormatter: ReturnType<typeof useCurrency>
  currencyIso?: string
  t: any
  infoComp: React.ReactNode
  getTranslations: PromotedFlightCardItemProps['getTranslations']
  onFlightClick: (index: number, type: string) => void
  getVisaStatus: (countryCode: string) => boolean | undefined
  seeAllButtonProps: Omit<SeeAllButtonProps, 'mobileBlock'>
}

const TabContent: React.FC<TabContentProps> = ({
  data,
  activeTab,
  activeTabType,
  christmasFlightIds,
  isMobile,
  isTablet,
  isRTL,
  isWingie,
  isAnniversary,
  isClient,
  market,
  currencyFormatter,
  currencyIso,
  t,
  infoComp,
  getTranslations,
  onFlightClick,
  getVisaStatus,
  seeAllButtonProps,
}) => {
  const isVisaFreeTab = activeTabType === 'visaFree'
  const preloadCount = getPreloadCount(!!isMobile, !!isTablet)

  const cards = data?.map((flightData, index) => {
    const isChristmasFlight = christmasFlightIds.has(flightData.id)
    const shouldShowChristmasBadge = !isWingie && (activeTabType === 'christmas' || (isVisaFreeTab && isChristmasFlight))
    const visaFreeStatus = isWingie ? undefined : getVisaStatus(flightData.destinationCountryCode || '')

    return (
      <PromotedFlightCardItem
        key={flightData.id}
        flightData={flightData}
        index={index}
        isPreload={index < preloadCount}
        isMobile={!!isMobile}
        isRTL={isRTL}
        isWingie={isWingie}
        market={market}
        shouldShowChristmasBadge={shouldShowChristmasBadge}
        visaFreeStatus={visaFreeStatus}
        activeTabType={activeTabType || ''}
        currencyFormatter={currencyFormatter}
        currencyIso={currencyIso}
        isClient={isClient}
        t={t}
        getTranslations={getTranslations}
        onFlightClick={onFlightClick}
      />
    )
  })

  let carousel: React.ReactNode
  if (isTablet) {
    carousel = (
      <SnapCarousel key={activeTab} isRTL={isRTL} options={SNAP_CAROUSEL_TABLET_OPTIONS} dataTestId="promoted-flight-tablet-carousel" containerWidth={1120} isMobile={false}>
        {cards}
      </SnapCarousel>
    )
  } else if (isMobile) {
    carousel = (
      <SnapCarousel key={activeTab} isRTL={isRTL} options={SNAP_CAROUSEL_OPTIONS} dataTestId="finance-carousel" containerWidth={1120} isMobile={true}>
        {cards}
      </SnapCarousel>
    )
  } else {
    carousel = (
      <Slider key={activeTab} options={SLIDER_OPTIONS} customElement={isClient ? infoComp : null} isRTL={isRTL}>
        {cards}
      </Slider>
    )
  }

  return (
    <>
      {carousel}
      {(isMobile || isTablet) && isClient && infoComp}
      {isMobile && isAnniversary && <SeeAllButton {...seeAllButtonProps} mobileBlock />}
    </>
  )
}

const PromotedFlights: React.FC<PromotedFlightsProps> = ({ promotedFlightsData, visaFreeCountries = {} }) => {
  const { currency, market, endpoint } = useAppState()
  const currencyFormatter = useCurrency()
  const isMobile = useIsMobile()
  const { isTablet } = useDeviceType()
  const isClient = useIsClient()
  const { t } = useTranslation('common')
  const [activeTab, setActiveTab] = useState<number>(0)
  const [showModal, setShowModal] = useState<boolean>(false)
  const isWingie = market !== 'tr'
  /** 10. yıl anniversary UI — TR'de yok, eski Promoted Flights görünümü */
  const isAnniversary = isWingie

  // Vize durumunu prop'tan gelen veriden al
  const getVisaStatus = (countryCode: string): boolean | undefined => {
    return visaFreeCountries[countryCode]?.isVisaFree
  }

  // Vize tipini prop'tan gelen veriden al
  const getVisaType = (countryCode: string): string | undefined => {
    return visaFreeCountries[countryCode]?.visaType
  }

  const language = i18n?.language
  const isRTL = i18n?.dir(language?.includes('-') ? language.split('-')[1] : language) === 'rtl'

  const handleFlightClick = (index: number, type: string) => {
    console.log(`Tıklanan uçuş sırası: ${index + 1}, Tipi: ${type}`)

    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: 'homepage',
      page: 'homepage',
      type: 'click',
      message: `ctxt_selected_promoted_flight_order=${index + 1}`,
      requestId,
    })

    sendTrackerLog({
      category: 'homepage',
      page: 'homepage',
      type: 'click',
      message: `hm_promoted_flight_${type}`,
      requestId,
    })
  }

  // Önce temel veriyi kontrol et ve promotedFlights'ı başlat
  const promotedFlights = useMemo(() => {
    if (!promotedFlightsData) return []

    const { domestic = [], international = [], regional = [], christmas = [], visaFree = [], ramadanHolidayDeals = [] } = promotedFlightsData ?? []
    // Sıralama fonksiyonu
    const sortByCreatedAt = (a: any, b: any) => {
      const dateA = new Date(a.createdAt?.date).getTime()
      const dateB = new Date(b.createdAt?.date).getTime()
      return dateB - dateA // Yeniden eskiye sıralama
    }

    // Dizileri sırala ve filtrele
    const sortedDomestic = [...domestic].sort(sortByCreatedAt)
    const sortedInternational = [...international].sort(sortByCreatedAt)
    const sortedRegional = [...regional].sort(sortByCreatedAt)
    const sortedChristmas = [...christmas].sort(sortByCreatedAt)
    const sortedVisaFree = [...visaFree].sort(sortByCreatedAt)
    const sortedRamadanHolidayDeals = [...ramadanHolidayDeals].sort(sortByCreatedAt)
    const flights = [
      ...(market === 'tr' ? [{ type: 'christmas', flights: Array.isArray(sortedChristmas) && sortedChristmas?.length > 0 ? sortedChristmas : [] }] : []),
      { type: 'international', flights: Array.isArray(sortedInternational) && sortedInternational?.length >= 5 ? sortedInternational : [] },
      { type: 'domestic', flights: Array.isArray(sortedDomestic) && sortedDomestic?.length >= 5 ? sortedDomestic : [] },
      ...(market === 'tr' && Array.isArray(sortedVisaFree) && sortedVisaFree?.length >= 3 ? [{ type: 'visaFree', flights: sortedVisaFree }] : []),
      { type: 'regional', flights: Array.isArray(sortedRegional) && sortedRegional?.length >= 5 ? sortedRegional : [] },
      ...(market === 'tr' && Array.isArray(sortedRamadanHolidayDeals) && sortedRamadanHolidayDeals?.length >= 5
        ? [{ type: 'ramadanHolidayDeals', flights: sortedRamadanHolidayDeals }]
        : []),
    ].filter((item) => item.flights.length > 0)

    return flights
  }, [promotedFlightsData, market])

  // Vizesiz tab'ında christmas uçuşlarını kontrol etmek için christmas array'ini Set'e çevir
  const christmasFlightIds = useMemo(() => {
    if (promotedFlightsData?.christmas) {
      return new Set(promotedFlightsData.christmas.map((flight: PromotedFlight) => flight.id))
    }
    return new Set()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotedFlightsData?.christmas])

  // Tüm useMemo hook'ları artık erken return'lardan önce çağırabiliriz
  const infoComp = useMemo(() => {
    if (!isClient) return null

    const isChristmas = promotedFlights[activeTab]?.type === 'christmas'
    const infoButtonBg = isChristmas ? 'transparent' : '#F8FAF9'

    return (
      <S.InfoSpan data-testid="promoted-flight-info-span" dir={isRTL ? 'rtl' : 'ltr'}>
        {t(`promotedFlights.info`)}
        {!isMobile && (
          <S.InfoButton onClick={() => setShowModal((prev) => !prev)} data-testid="promoted-flight-info-button" aria-label={t('promotedFlights.infoButton')}>
            <InfoCircleOutline width={16} />
            <InfoDialog isOpen={showModal} onClose={() => setShowModal(false)} market={market} />
          </S.InfoButton>
        )}
        {(isMobile || isTablet) && (
          <Tooltip
            bottomSheet={true}
            isTablet={isTablet}
            popover={{ width: isTablet ? '400px' : '100px', popover: { placement: 'bottom' } }}
            button={
              <S.InfoButton aria-label={t('promotedFlights.infoButton')} style={{ backgroundColor: infoButtonBg }}>
                <InfoCircleOutline width={16} />
              </S.InfoButton>
            }
          >
            <S.ModalTitle data-testid="promoted-flight-mobile-modal-title">{t('promotedFlights.modal.title')}</S.ModalTitle>
            <ModalItemsList market={market} testIdPrefix="promoted-flight-mobile-modal-item" />
          </Tooltip>
        )}
      </S.InfoSpan>
    )
  }, [isClient, isMobile, isTablet, showModal, t, activeTab, promotedFlights, isRTL, market])

  const getTranslations = (countryCode?: string) => ({
    popularRoutesImage: (origin: string, destination: string) => (isClient ? t('popularRoutesImage', { origin, destination }) : ''),
    roundTrip: isClient ? t('promotedFlights.roundTrip') : '',
    oneWay: isClient ? t('promotedFlights.oneWay') : '',
    visaFreeText: (() => {
      if (!countryCode) return ''
      const visaType = getVisaType(countryCode)
      return visaType || ''
    })(),
  })

  // See All button fonksiyonları
  const shouldShowSeeAllButton = () => {
    return market === 'tr' || (isWingie && promotedFlights.length > 0)
  }

  const seeAllUrl = useMemo(() => {
    if (market === TR_MARKET) {
      return TR_BASE_URL
    }

    if (!isWingie) {
      return null
    }

    const sampleSearchUrl = findFirstPromotedFlightSearchUrl(promotedFlightsData)
    return buildPromotedFlightsSeeAllUrl(endpoint, sampleSearchUrl)
  }, [market, isWingie, endpoint, promotedFlightsData])

  const seeAllButtonProps: Omit<SeeAllButtonProps, 'mobileBlock'> = {
    isAnniversary,
    isMobile: !!isMobile,
    market,
    isWingie,
    hasPromotedFlights: promotedFlights.length > 0,
    seeAllUrl,
    t,
  }

  // Eğer hiç veri yoksa component'i render etme
  if (!promotedFlights || promotedFlights.length === 0) {
    return null
  }

  const activeTabType = promotedFlights[activeTab]?.type || ''

  return (
    <S.Wrapper $withConfetti={isAnniversary} isChristmasActive={isMobile && promotedFlights[activeTab]?.type === 'christmas'}>
      {isAnniversary ? (
        <>
          <S.DesktopGlow aria-hidden="true">
            <DesktopGlowBg />
          </S.DesktopGlow>
          <S.MobileGlow aria-hidden="true">
            <MobileGlowBg />
          </S.MobileGlow>
          <ConfettiOnce enabled>
            <S.ConfettiLayer aria-hidden="true">
              <ConfettiDecoration position="top" />
              <ConfettiDecoration position="bottom" />
            </S.ConfettiLayer>
          </ConfettiOnce>
          <S.Foreground>
            <S.HeaderSection>
              <Celebrating10YearsBadge />
              <S.HeaderTopRow>
                <S.TitleBlock>
                  <S.PromotedHeading $anniversary data-testid="promoted-flight-heading">
                    {highlightAnniversaryTens(t('promotedFlights.anniversaryHeading'))}
                  </S.PromotedHeading>
                  <S.PromotedSubheading data-testid="promoted-flight-subheading">{t('promotedFlights.anniversarySubheading')}</S.PromotedSubheading>
                </S.TitleBlock>
              </S.HeaderTopRow>
              <S.TabsRow dir={isRTL ? 'rtl' : 'ltr'}>
                <S.TabsContainer>
                  {promotedFlights.map((item, index) => (
                    <S.TabsButton
                      key={item.type}
                      active={activeTab === index}
                      isChristmas={item.type === 'christmas' && activeTab === index}
                      onClick={() => setActiveTab(index)}
                      data-testid={`promoted-flight-tab-button-${index}`}
                    >
                      {t(`promotedFlights.${item.type}.heading`)}
                    </S.TabsButton>
                  ))}
                </S.TabsContainer>
                {!isMobile && <SeeAllButton {...seeAllButtonProps} />}
              </S.TabsRow>
            </S.HeaderSection>
            <S.PromotedFlightWrapper data-testid="promoted-flight-wrapper">
              {promotedFlights[activeTab] && (
                <TabContent
                  data={promotedFlights[activeTab].flights}
                  activeTab={activeTab}
                  activeTabType={activeTabType}
                  christmasFlightIds={christmasFlightIds}
                  isMobile={!!isMobile}
                  isTablet={!!isTablet}
                  isRTL={isRTL}
                  isWingie={isWingie}
                  isAnniversary={isAnniversary}
                  isClient={isClient}
                  market={market}
                  currencyFormatter={currencyFormatter}
                  currencyIso={currency?.iso}
                  t={t}
                  infoComp={infoComp}
                  getTranslations={getTranslations}
                  onFlightClick={handleFlightClick}
                  getVisaStatus={getVisaStatus}
                  seeAllButtonProps={seeAllButtonProps}
                />
              )}
            </S.PromotedFlightWrapper>
          </S.Foreground>
        </>
      ) : (
        <>
          <S.HeaderContainer>
            <S.HeaderWrapper>
              <S.PromotedHeading data-testid="promoted-flight-heading">
                <S.PromotedIcon src={PROMOTED_ICON_URL} alt="Promoted Flights" />
                {t(`promotedFlights.heading`)}
              </S.PromotedHeading>
            </S.HeaderWrapper>
            <S.TabsContainer>
              {promotedFlights.map((item, index) => (
                <S.TabsButton
                  key={item.type}
                  active={activeTab === index}
                  isChristmas={item.type === 'christmas' && activeTab === index}
                  onClick={() => setActiveTab(index)}
                  data-testid={`promoted-flight-tab-button-${index}`}
                >
                  {t(`promotedFlights.${item.type}.heading`)}
                </S.TabsButton>
              ))}
              {isMobile && shouldShowSeeAllButton() && seeAllUrl && (
                <S.SeeAllLink href={seeAllUrl} data-testid="promoted-flight-see-all-link">
                  {t('promotedFlights.seeAll')}
                </S.SeeAllLink>
              )}
            </S.TabsContainer>
            {!isMobile && <SeeAllButton {...seeAllButtonProps} />}
          </S.HeaderContainer>
          <S.PromotedFlightWrapper data-testid="promoted-flight-wrapper">
            {promotedFlights[activeTab] && (
              <TabContent
                data={promotedFlights[activeTab].flights}
                activeTab={activeTab}
                activeTabType={activeTabType}
                christmasFlightIds={christmasFlightIds}
                isMobile={!!isMobile}
                isTablet={!!isTablet}
                isRTL={isRTL}
                isWingie={isWingie}
                isAnniversary={isAnniversary}
                isClient={isClient}
                market={market}
                currencyFormatter={currencyFormatter}
                currencyIso={currency?.iso}
                t={t}
                infoComp={infoComp}
                getTranslations={getTranslations}
                onFlightClick={handleFlightClick}
                getVisaStatus={getVisaStatus}
                seeAllButtonProps={seeAllButtonProps}
              />
            )}
          </S.PromotedFlightWrapper>
        </>
      )}
    </S.Wrapper>
  )
}

export default PromotedFlights
